import React, { useMemo, useState } from 'react';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTransportCloudContext } from 'contexts/transportCloudContext';
import { generatePath, useNavigate } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import * as S from '../../styles';
import { BUTTONS, DIALOGS, ERRORS } from '../../TransportCloudTable.consts';
import { MailType } from '../Dialogs/MailSenderDialog';

const USER_COLORS = [
  '#FF5252',
  '#4CAF50',
  '#2196F3',
  '#FF9800',
  '#9C27B0',
  '#00BCD4',
  '#FFEB3B',
  '#E91E63',
  '#8BC34A',
  '#FFC107',
];

const MAX_VISIBLE_USERS = 5;

const getColorForUser = (username: string): string => {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % USER_COLORS.length;
  return USER_COLORS[index];
};

const formatUserDisplay = (username: string) => {
  if (username.length === 3 && username === username.toUpperCase()) {
    return { display: username, tooltip: username };
  }
  return { display: username.charAt(0).toUpperCase(), tooltip: username };
};

interface MailSenderConditions {
  hasPermission: boolean;
  hasSelection: boolean;
  allJobRows: boolean;
  sameJob: boolean;
  allHaveTrucker: boolean;
  ownerAuthorized: boolean;
}

interface ActiveUsersProps {
  showUsers?: boolean;
  currentUser?: string;
  selectedCount?: number;
  isDeleting?: boolean;
  hasAnyDeletePrivilege?: boolean;
  hasUnauthorizedSelectedItems?: boolean;
  hasAddShipmentPlansPrivilege?: boolean;
  showMailSender?: boolean;
  isMailSenderEnabled?: boolean;
  mailSenderConditions?: MailSenderConditions;
  isNotificationEnabled?: boolean;
  isWaybillEnabled?: boolean;
  onDeleteClick?: () => void;
  onMailSenderClick?: (type: MailType) => void;
}

const TableTools: React.FC<ActiveUsersProps> = ({
  showUsers = false,
  currentUser = '',
  selectedCount = 0,
  isDeleting = false,
  hasAnyDeletePrivilege = true,
  hasUnauthorizedSelectedItems = false,
  hasAddShipmentPlansPrivilege = true,
  showMailSender = false,
  isMailSenderEnabled = false,
  mailSenderConditions,
  isNotificationEnabled = false,
  isWaybillEnabled = false,
  onDeleteClick,
  onMailSenderClick,
}) => {
  const navigate = useNavigate();
  const { activeUsers } = useTransportCloudContext();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);
  const handleMenuSelect = (type: MailType) => {
    handleMenuClose();
    onMailSenderClick?.(type);
  };

  const addEntryHandler = () => {
    navigate(generatePath(reactPaths.transportCloudForm));
  };

  const filteredUsers = activeUsers.filter(
    (username) => username !== currentUser,
  );

  const users = filteredUsers.map((username) => ({
    username,
    ...formatUserDisplay(username),
    color: getColorForUser(username),
  }));

  const shouldShowMoreCircle = users.length > MAX_VISIBLE_USERS;
  const visibleUsers = shouldShowMoreCircle
    ? users.slice(0, MAX_VISIBLE_USERS - 1)
    : users;
  const hiddenUsers = shouldShowMoreCircle
    ? users.slice(MAX_VISIBLE_USERS - 1)
    : [];
  const hiddenUsersNames = hiddenUsers.map((user) => user.tooltip).join(', ');

  const mailSenderTooltipLines = useMemo(() => {
    if (!mailSenderConditions || isMailSenderEnabled) return [];
    if (!mailSenderConditions.hasSelection) return [];

    const lines: string[] = [];
    if (!mailSenderConditions.hasPermission)
      lines.push(ERRORS.mailSenderNoPermission);
    if (!mailSenderConditions.allJobRows)
      lines.push(ERRORS.mailSenderNotAllJobRows);
    if (!mailSenderConditions.sameJob) lines.push(ERRORS.mailSenderNotSameJob);
    if (!mailSenderConditions.allHaveTrucker)
      lines.push(ERRORS.mailSenderNoTrucker);
    if (!mailSenderConditions.ownerAuthorized)
      lines.push(ERRORS.mailSenderNotOwner);
    return lines;
  }, [mailSenderConditions, isMailSenderEnabled]);

  return (
    <S.ActionsContainer style={{ width: '100%' }}>
      <Box style={{ display: 'flex', gap: '8px' }}>
        {hasAddShipmentPlansPrivilege && (
          <S.TCStyledButton $primary onClick={addEntryHandler}>
            {BUTTONS.addEntry}
          </S.TCStyledButton>
        )}
        {showMailSender && (
          <>
            <Tooltip
              title={
                mailSenderTooltipLines.length > 0 ? (
                  mailSenderTooltipLines.length === 1 ? (
                    mailSenderTooltipLines[0]
                  ) : (
                    <Box
                      component='ul'
                      sx={{
                        m: 0,
                        p: 0,
                        listStyle: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                      }}
                    >
                      {mailSenderTooltipLines.map((line) => (
                        <Box
                          component='li'
                          key={line}
                          sx={{
                            display: 'flex',
                            gap: '6px',
                            alignItems: 'flex-start',
                          }}
                        >
                          <span>•</span>
                          <span>{line}</span>
                        </Box>
                      ))}
                    </Box>
                  )
                ) : (
                  ''
                )
              }
              placement='bottom'
              arrow
              componentsProps={{ tooltip: { sx: { fontSize: '0.7rem' } } }}
            >
              <span>
                <S.TCStyledButton
                  $primary
                  disabled={!isMailSenderEnabled}
                  onClick={isMailSenderEnabled ? handleMenuOpen : undefined}
                  style={{ opacity: isMailSenderEnabled ? 1 : 0.5 }}
                >
                  {BUTTONS.send}
                </S.TCStyledButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={handleMenuClose}
            >
              <Tooltip
                title={
                  !isNotificationEnabled
                    ? DIALOGS.notificationDisabledTooltip
                    : ''
                }
                placement='left'
                arrow
              >
                <span>
                  <MenuItem
                    onClick={() => handleMenuSelect('notification')}
                    disabled={!isNotificationEnabled}
                  >
                    {DIALOGS.notificationLabel}
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip
                title={!isWaybillEnabled ? DIALOGS.waybillDisabledTooltip : ''}
                placement='left'
                arrow
              >
                <span>
                  <MenuItem
                    onClick={() => handleMenuSelect('waybill')}
                    disabled={!isWaybillEnabled}
                  >
                    {DIALOGS.waybillLabel}
                  </MenuItem>
                </span>
              </Tooltip>
            </Menu>
          </>
        )}
        {hasAnyDeletePrivilege &&
          (hasUnauthorizedSelectedItems ? (
            <Tooltip
              title={ERRORS.noPrivilegeDeleteDisabled}
              arrow
              placement='top'
              componentsProps={{ tooltip: { sx: { fontSize: '0.7rem' } } }}
            >
              <span>
                <S.TCStyledButton
                  $primary
                  disabled
                  style={{ opacity: 0.5, pointerEvents: 'none' }}
                >
                  {BUTTONS.delete}
                </S.TCStyledButton>
              </span>
            </Tooltip>
          ) : (
            <S.TCStyledButton
              $primary
              disabled={selectedCount === 0 || isDeleting}
              onClick={onDeleteClick}
              style={{
                opacity: selectedCount === 0 || isDeleting ? 0.5 : 1,
                cursor:
                  selectedCount === 0 || isDeleting ? 'not-allowed' : 'pointer',
              }}
            >
              {BUTTONS.delete}
            </S.TCStyledButton>
          ))}
      </Box>

      {showUsers && (
        <S.UsersContainer>
          {visibleUsers.map((user) => (
            <Tooltip
              key={user.username}
              title={user.tooltip}
              arrow
              placement='top'
              componentsProps={{
                tooltip: { sx: { fontSize: '0.7rem', fontWeight: 500 } },
              }}
              PopperProps={{
                modifiers: [{ name: 'offset', options: { offset: [0, -5] } }],
              }}
            >
              <S.UserCircle $backgroundColor={user.color}>
                {user.display}
              </S.UserCircle>
            </Tooltip>
          ))}

          {shouldShowMoreCircle && (
            <Tooltip
              title={hiddenUsersNames}
              arrow
              placement='top'
              componentsProps={{
                tooltip: { sx: { fontSize: '0.7rem', fontWeight: 500 } },
              }}
              PopperProps={{
                modifiers: [{ name: 'offset', options: { offset: [0, -5] } }],
              }}
            >
              <S.UserCircle>+{hiddenUsers.length}</S.UserCircle>
            </Tooltip>
          )}
        </S.UsersContainer>
      )}
    </S.ActionsContainer>
  );
};

export { TableTools };
